import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Slider,
  IconButton,
  Typography,
  TextField,
  FormControlLabel,
  Switch
} from '@mui/material';
import { PlayArrow, Pause } from '@mui/icons-material';
import { formatMsToPattern } from './timeFormat';

interface VideoPlayerProps {
  src: string;
  showLoopControls?: boolean;
  format?: string;
}

const formatTime = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000);
  const milliseconds = Math.floor((ms % 1000) / 10);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(
    seconds
  ).padStart(2, '0')}.${String(milliseconds).padStart(2, '0')}`;
};

const parseTime = (timeStr: string): number => {
  const match = timeStr.match(/^(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,2}))?$/);
  if (!match) return 0;
  const [, hh, mm, ss, ms = '0'] = match;
  return (
    parseInt(hh) * 3600000 +
    parseInt(mm) * 60000 +
    parseInt(ss) * 1000 +
    parseInt(ms.padEnd(2, '0')) * 10
  );
};

const normalizeTimeInput = (input: string): string => {
  const parts = input.split(':');
  while (parts.length < 3) parts.unshift('0');
  const [hh, mm, rest] = parts;
  const [ss, ms = '00'] = rest.split('.');
  return `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}:${ss.padStart(2, '0')}.${ms.padEnd(2, '0')}`;
};

const isValidTimeFormat = (input: string): boolean => {
  return /^(\d{1,2}):(\d{1,2}):(\d{1,2})(\.\d{1,2})?$/.test(input);
};

const VideoPlayer: React.FC<VideoPlayerProps> = ({ src, showLoopControls, format }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [trimStartInput, setTrimStartInput] = useState('00:00:00.00');
  const [trimEndInput, setTrimEndInput] = useState('00:00:00.00');
  const [trimStartError, setTrimStartError] = useState(false);
  const [trimEndError, setTrimEndError] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateDuration = () => {
      const fullDuration = video.duration * 1000;
      setDuration(fullDuration);
      setTrimEnd((prev) => (prev === 0 ? fullDuration : prev));
      setTrimEndInput((prev) =>
        prev === '00:00:00.00' || parseTime(prev) === 0
          ? formatTime(fullDuration)
          : prev
      );
    };

    const updateTime = () => {
      const currentMs = (videoRef.current?.currentTime || 0) * 1000;
      setCurrentTime(currentMs);

      if (loopEnabled && currentMs >= trimEnd) {
        const video = videoRef.current!;
        video.currentTime = trimStart / 1000;
        setCurrentTime(trimStart);
        if (isPlaying) {
          video.play().catch((err) => console.warn("Playback failed:", err));
        }
      }
    };

    const handleError = () => {
      // Surfaces the real MediaError (e.g. src 404/CORS/expired URL) instead of a generic play() rejection
      console.error('Video failed to load:', video.error, 'src:', video.currentSrc);
      setIsPlaying(false);
    };

    video.addEventListener('loadedmetadata', updateDuration);
    video.addEventListener('timeupdate', updateTime);
    video.addEventListener('error', handleError);

    return () => {
      video.removeEventListener('loadedmetadata', updateDuration);
      video.removeEventListener('timeupdate', updateTime);
      video.removeEventListener('error', handleError);
    };
  }, [loopEnabled, trimStart, trimEnd]);

  const play = () => {
    if (videoRef.current) {
      if (loopEnabled) {
        videoRef.current.currentTime = trimStart / 1000;
        setCurrentTime(trimStart);
      }
      videoRef.current
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => {
          console.error('Video playback failed (check that src is reachable/CORS-enabled):', err);
          setIsPlaying(false);
        });
    }
  };

  const pause = () => {
    videoRef.current?.pause();
    setIsPlaying(false);
  };

  const seek = (ms: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = ms / 1000;
      setCurrentTime(ms);
    }
  };

  const handleSeek = (_: Event, value: number | number[]) => {
    if (typeof value === 'number') {
      seek(value);
    }
  };

  const handleTrimStartChange = (value: string) => {
    setTrimStartInput(value);

    if (!isValidTimeFormat(value)) {
      setTrimStartError(true);
      return;
    }

    setTrimStartError(false);
    const normalized = normalizeTimeInput(value);
    setTrimStartInput(normalized);

    const ms = parseTime(normalized);
    if (!isNaN(ms) && ms < trimEnd) {
      setTrimStart(ms);
    }
  };

  const handleTrimEndChange = (value: string) => {
    setTrimEndInput(value);

    if (!isValidTimeFormat(value)) {
      setTrimEndError(true);
      return;
    }

    setTrimEndError(false);
    const normalized = normalizeTimeInput(value);
    setTrimEndInput(normalized);

    const ms = parseTime(normalized);
    if (!isNaN(ms) && ms > trimStart && ms <= duration) {
      setTrimEnd(ms);
    }
  };

  return (
    <Box p={2} border="1px solid #ccc" borderRadius={2}>
      <video ref={videoRef} src={src} preload="metadata" width="100%" controls={false} />

      <Box display="flex" alignItems="center" gap={2} mt={2}>
        <IconButton onClick={isPlaying ? pause : play}>
          {isPlaying ? <Pause /> : <PlayArrow />}
        </IconButton>

        <Typography variant="body2">{formatMsToPattern(currentTime, format)}</Typography>

        <Box sx={{ position: 'relative', flex: 1 }}>
          {loopEnabled && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                height: 8,
                backgroundColor: 'rgba(33, 150, 243, 0.3)',
                transform: 'translateY(-50%)',
                left: `${(trimStart / duration) * 100}%`,
                width: `${Math.max(((trimEnd - trimStart) / duration) * 100, 1)}%`,
                borderRadius: 2,
                pointerEvents: 'none',
                zIndex: 1
              }}
            />
          )}
          <Slider
            value={Math.min(currentTime, duration)}
            min={0}
            max={duration}
            onChange={handleSeek}
            step={10}
            marks={
              loopEnabled
                ? [
                  { value: trimStart, label: formatMsToPattern(trimStart, format) },
                  { value: trimEnd, label: formatMsToPattern(trimEnd, format) }
                ]
                : []
            }
          />
        </Box>

        <Typography variant="body2">{formatMsToPattern(duration, format)}</Typography>
      </Box>

      {showLoopControls && (
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={loopEnabled}
                onChange={(e) => setLoopEnabled(e.target.checked)}
              />
            }
            label="Loop Trim Section"
          />

          <TextField
            label="Trim Start"
            size="small"
            defaultValue={trimStartInput}
            onChange={(e) => handleTrimStartChange(e.target.value)}
            error={trimStartError}
            helperText={trimStartError ? 'Format: HH:MM:SS.mm' : ''}
            placeholder="00:00:00.00"
            inputProps={{ maxLength: 11 }}
          />

          <TextField
            label="Trim End"
            size="small"
            defaultValue={trimEndInput}
            onChange={(e) => handleTrimEndChange(e.target.value)}
            error={trimEndError}
            helperText={trimEndError ? 'Format: HH:MM:SS.mm' : ''}
            placeholder="00:00:00.00"
            inputProps={{ maxLength: 11 }}
          />
        </Box>
      )}
    </Box>
  );
};

export default VideoPlayer;